import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, X, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

const STORAGE_KEY = "rp_lead_modal_v1";
const DELAY_MS = 12_000; // 12 seconds
const SUPPRESS_DAYS = 14;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function isSuppressed() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const { at } = JSON.parse(raw);
    if (!at) return false;
    const ageMs = Date.now() - new Date(at).getTime();
    return ageMs < SUPPRESS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function suppress(reason) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ reason, at: new Date().toISOString() })
    );
  } catch {}
}

export default function LeadModal() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (isSuppressed()) return;
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    setOpen(false);
    suppress(submitted ? "submitted" : "dismissed");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/subscribe`, {
        email: email.trim(),
        phone: phone.trim() || null,
        source: "lead_modal",
      });
      setSubmitted(true);
      suppress("submitted");
      toast.success("You're on the list.", {
        description: "A technician will reach out within one business hour.",
      });
      setTimeout(() => setOpen(false), 1200);
    } catch (err) {
      toast.error("Couldn't submit", {
        description: "Please try again or call us directly.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogContent
        data-testid="lead-modal"
        className="sm:max-w-md p-0 overflow-hidden border-border"
      >
        <button
          data-testid="lead-modal-close"
          aria-label="Close"
          onClick={close}
          className="absolute top-3 right-3 z-10 h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-secondary text-muted-foreground"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="bg-primary text-primary-foreground px-6 py-7 md:px-8 md:py-8">
          <div className="h-12 w-12 rounded-full bg-primary-foreground/10 flex items-center justify-center">
            <Bell className="h-6 w-6" />
          </div>
          <DialogTitle className="font-heading text-2xl md:text-[26px] font-bold tracking-tight leading-tight mt-5">
            Stuck with a slow PC?<br />Get a free diagnostic.
          </DialogTitle>
          <DialogDescription className="text-sm md:text-[15px] text-primary-foreground/80 mt-3 leading-relaxed">
            Leave your email and we'll send a free written diagnostic plan
            for your device — plus occasional updates on security alerts
            our technicians track. Unsubscribe any time.
          </DialogDescription>
        </div>

        <form onSubmit={onSubmit} className="px-6 py-6 md:px-8 md:py-7 space-y-4 bg-card">
          <div>
            <label htmlFor="lead-email" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Email <span className="text-destructive">*</span>
            </label>
            <Input
              id="lead-email"
              data-testid="lead-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="mt-1.5"
            />
          </div>
          <div>
            <label htmlFor="lead-phone" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Phone <span className="text-muted-foreground/60 font-normal normal-case ml-1">(optional)</span>
            </label>
            <Input
              id="lead-phone"
              data-testid="lead-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 (555) 123-4567"
              className="mt-1.5"
            />
          </div>
          <Button
            type="submit"
            disabled={sending || submitted}
            data-testid="lead-submit"
            className="w-full h-11 font-semibold bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {sending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
            ) : submitted ? (
              <>You're in ✓</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" /> Send me a free diagnostic</>
            )}
          </Button>
          <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
            No spam. We email at most twice a month and you can unsubscribe in one click.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
